"use client";

import React from "react";
import { type SpinningWheelUIReward } from "@cede/types";
import { isBigPrize, getRewardCssVar, getRewardIcon } from "@/utils/wheel";

interface WheelRewardCardProps {
  reward: SpinningWheelUIReward;
  slideDirection: "left" | "right" | null;
  prizeNumber?: number;
  totalPrizes?: number;
}

/**
 * Displays a single reward card with animations
 */
export const WheelRewardCard: React.FC<WheelRewardCardProps> = ({
  reward,
  slideDirection,
  prizeNumber,
  totalPrizes,
}) => {
  const isBig = isBigPrize(reward.rewardAmount, reward.rewardSymbol);
  const Icon = getRewardIcon(reward.rewardSymbol, { width: 48, height: 48 });

  const cssVarName = getRewardCssVar(reward.rewardSymbol);

  return (
    <div className="flex flex-col items-center text-center gap-3">
      {/* Congratulations Header */}
      <div className="flex flex-col gap-1">
        <h2 className="text-3xl font-bold text-cede-primary">{isBig ? "🎉 JACKPOT! 🎉" : "Congratulations!"}</h2>
        <p className="text-sm text-cede-text-secondary">{isBig ? "You hit the grand prize!" : "You won"}</p>
      </div>

      {/* Reward Card */}
      <div
        className={`relative rounded-2xl p-8 border-2 w-full max-w-sm transition-all duration-700 ease-out ${
          isBig ? "animate-pulse-glow" : ""
        } ${slideDirection === "left" ? "opacity-0 translate-x-16" : ""} ${
          slideDirection === "right" ? "opacity-0 -translate-x-16" : ""
        } ${!slideDirection ? "opacity-100 translate-x-0" : ""}`}
        style={
          {
            "background": `linear-gradient(to bottom right, color-mix(in srgb, var(${cssVarName}) 20%, transparent), color-mix(in srgb, var(${cssVarName}) 5%, transparent))`,
            "borderColor": `var(${cssVarName})`,
            "boxShadow": isBig
              ? `0 0 30px color-mix(in srgb, var(${cssVarName}) 50%, transparent), 0 0 60px color-mix(in srgb, var(${cssVarName}) 25%, transparent)`
              : "none",
            "--glow-color": `color-mix(in srgb, var(${cssVarName}) 50%, transparent)`,
            "--glow-color-dim": `color-mix(in srgb, var(${cssVarName}) 25%, transparent)`,
          } as React.CSSProperties
        }
      >
        {/* Prize Number Badge */}
        {prizeNumber !== undefined && totalPrizes !== undefined && (
          <div
            className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-semibold"
            style={{
              backgroundColor: `color-mix(in srgb, var(${cssVarName}) 20%, transparent)`,
              color: `var(${cssVarName})`,
              border: `1px solid color-mix(in srgb, var(${cssVarName}) 40%, transparent)`,
            }}
          >
            Reward #{prizeNumber}
          </div>
        )}

        {/* Icon and Amount */}
        <div className="flex flex-col items-center gap-4 mt-8">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `color-mix(in srgb, var(${cssVarName}) 12%, transparent)` }}
          >
            {Icon}
          </div>

          <div className="flex flex-col items-center gap-2">
            <div className="text-5xl font-bold" style={{ color: `var(${cssVarName})` }}>
              {reward.rewardAmount.toNumber()} {reward.rewardSymbol}
            </div>

            {/* Additional message for big prizes */}
            {isBig && <p className="text-xs text-cede-text-secondary">Share on Twitter to claim your reward</p>}
          </div>
        </div>
      </div>

      {/* Delivery information - outside card */}
      <p className="text-sm text-cede-text-secondary">Reward will be sent within 24 hours</p>
    </div>
  );
};
